import React, { useEffect, useState, useRef } from 'react';
import { motion, HTMLMotionProps } from 'framer-motion';
import { Loader2, Command } from 'lucide-react';
import { cn } from '../../utils/cn';
interface ButtonProps extends Omit<HTMLMotionProps<'button'>, 'children'> {
  variant?: 'primary' | 'secondary' | 'terracotta' | 'outline' | 'ghost' | 'danger' | 'white' | 'gradient' | 'soft' | 'info' | 'success';
  size?: 'sm' | 'md' | 'lg' | 'xl';
  fullWidth?: boolean;
  isLoading?: boolean;
  loadingText?: string;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  children: React.ReactNode;
  pulse?: boolean;
  glow?: boolean;
  shortcut?: string;
  withRipple?: boolean;
}
export function Button({
  variant = 'primary',
  size = 'md',
  fullWidth = false,
  isLoading = false,
  loadingText,
  leftIcon,
  rightIcon,
  children,
  className = '',
  disabled,
  pulse = false,
  glow = false,
  shortcut,
  withRipple = true,
  onClick,
  ...props
}: ButtonProps) {
  const [ripples, setRipples] = useState<{
    x: number;
    y: number;
    id: number;
  }[]>([]);
  const buttonRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (ripples.length > 0) {
      const timer = setTimeout(() => {
        setRipples(prev => prev.slice(1));
      }, 600);
      return () => clearTimeout(timer);
    }
  }, [ripples]);
  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (disabled || isLoading) return;
    if (withRipple && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      setRipples(prev => [...prev, {
        x,
        y,
        id: Date.now()
      }]);
    }
    onClick?.(e);
  };
  const baseStyles = 'group inline-flex items-center justify-center font-bold rounded-xl transition-all duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-600 focus-visible:ring-offset-2 disabled:opacity-50 disabled:grayscale disabled:cursor-not-allowed relative overflow-hidden select-none tracking-wide active:scale-[0.98]';
  const variants = {
    primary: 'bg-primary-600 text-white shadow-soft-md border border-transparent hover:bg-primary-700 hover:shadow-soft-lg hover:shadow-primary-600/20',
    secondary: 'bg-charcoal-800 text-white shadow-soft-md border border-transparent hover:bg-charcoal-900 hover:shadow-soft-lg',
    terracotta: 'bg-secondary-500 text-white shadow-soft-md border border-transparent hover:bg-secondary-600 hover:shadow-soft-lg hover:shadow-secondary-500/20',
    outline: 'bg-transparent border-2 border-primary-600/30 text-primary-700 hover:bg-primary-50/50 hover:border-primary-600 hover:text-primary-800',
    ghost: 'bg-transparent text-primary-700 hover:bg-primary-50/50 hover:text-primary-800 border border-transparent',
    danger: 'bg-red-600 text-white shadow-soft-md border border-transparent hover:bg-red-700 hover:shadow-soft-lg',
    white: 'bg-white text-charcoal-900 shadow-soft-sm border border-gray-200 hover:shadow-soft-md hover:border-gray-300 hover:bg-gray-50',
    gradient: 'bg-gradient-to-r from-primary-700 to-primary-600 text-white shadow-soft-md border border-transparent hover:from-primary-800 hover:to-primary-700 hover:shadow-soft-lg',
    soft: 'bg-primary-50 text-primary-800 border border-primary-100 hover:bg-primary-100 hover:border-primary-200 shadow-soft-xs',
    info: 'bg-blue-50 text-blue-700 border border-blue-100 hover:bg-blue-100 hover:border-blue-200 shadow-soft-xs',
    success: 'bg-emerald-600 text-white shadow-soft-md border border-transparent hover:bg-emerald-700 hover:shadow-soft-lg'
  };
  const sizes = {
    sm: 'px-4 py-2 text-xs gap-1.5 min-h-[2.25rem]',
    md: 'px-6 py-2.5 text-sm gap-2 min-h-[2.75rem]',
    lg: 'px-8 py-3.5 text-base gap-2.5 min-h-[3.25rem]',
    xl: 'px-10 py-4 text-lg gap-3 min-h-[3.75rem]'
  };
  const classes = cn(baseStyles, variants[variant], sizes[size], fullWidth && 'w-full', className);
  return <motion.button ref={buttonRef} className={classes} disabled={disabled || isLoading} onClick={handleClick} whileHover={{
    y: disabled ? 0 : -2,
    scale: disabled ? 1 : 1.02,
    transition: {
      duration: 0.2,
      ease: 'easeOut'
    }
  }} whileTap={{
    scale: 0.97,
    y: 0,
    transition: {
      duration: 0.1
    }
  }} {...props} data-id="element-5197">
      {/* Ripple Effect */}
      {ripples.map(ripple => <span key={ripple.id} className="absolute rounded-full bg-white/30 animate-ripple pointer-events-none" style={{
      left: ripple.x,
      top: ripple.y,
      width: '20px',
      height: '20px',
      transform: 'translate(-50%, -50%)'
    }} data-id="element-5198" />)}

      {/* Glow Effect */}
      {glow && !disabled && <div className="absolute inset-0 rounded-xl bg-white/20 blur-lg opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" data-id="element-5199" />}

      {/* Pulse Effect for CTAs */}
      {pulse && !disabled && !isLoading && <span className="absolute inset-0 rounded-xl animate-pulse opacity-[0.1] bg-current pointer-events-none" data-id="element-5200" />}

      {/* Loading Shimmer Background */}
      {isLoading && <div className="absolute inset-0 bg-gradient-shimmer animate-shimmer opacity-30 pointer-events-none" data-id="element-5201" />}

      <span className={`relative z-10 flex items-center justify-center gap-2 ${isLoading ? 'opacity-0' : 'opacity-100'} transition-opacity duration-200`} data-id="element-5202">
        {leftIcon && <span className="flex-shrink-0 group-hover:-translate-x-0.5 transition-transform duration-300" data-id="element-5203">
            {leftIcon}
          </span>}

        {children}

        {rightIcon && <span className="flex-shrink-0 group-hover:translate-x-0.5 transition-transform duration-300" data-id="element-5204">
            {rightIcon}
          </span>}

        {shortcut && <span className="ml-2 hidden lg:inline-flex items-center gap-0.5 text-[10px] font-medium opacity-60 bg-black/10 px-1.5 py-0.5 rounded border border-white/10" data-id="element-5205">
            <Command className="w-3 h-3" data-id="element-5206" /> {shortcut}
          </span>}
      </span>

      {isLoading && <div className="absolute inset-0 flex items-center justify-center z-20 gap-2" data-id="element-5207">
          <Loader2 className="h-5 w-5 animate-spin" data-id="element-5208" />
          {loadingText && <span className="text-sm font-medium" data-id="element-5209">{loadingText}</span>}
        </div>}
    </motion.button>;
}